import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { ContactFormStore, ContactFormValue } from '../contact/contact-form.store';
import { OwnerRelationship, SignupAddress, SignupAddressLookup, SignupAddressStore } from '../address/signup-address.store';
import { SignupEnrollmentService } from '../enroll/signup-enrollment.service';

/** Key on /signup's history entry (`history.state`) holding the page as it was submitted. */
export const SUBMITTED_SIGNUP_STATE = 'signupSubmitted';

/** Key on /enrolled's history entry, so Back/Forward to it (or a reload) reopens it; see enrolledGuard. */
export const ENROLLED_STATE = 'signupEnrolled';

/** What the submitted page showed: the stores as they stood, without the lookup's token. */
export interface SubmittedSignup {
  address: SignupAddress | null;
  lookup: Omit<SignupAddressLookup, 'token'> | null;
  ownerRelationship: OwnerRelationship | null;
  contact: ContactFormValue;
  /** When Form 50-162 was signed (ISO); null for the waitlist. */
  signedAt: string | null;
}

function withoutToken(lookup: SignupAddressLookup): Omit<SignupAddressLookup, 'token'> {
  const shown: Partial<SignupAddressLookup> = { ...lookup };
  delete shown.token;
  return shown as Omit<SignupAddressLookup, 'token'>;
}

/**
 * Lets Back from /enrolled return to /signup as it was submitted: frozen, with no new lookup (a lookup saves a new
 * intake and token). /enrolled clears the stores and `signup:` storage, so the page is kept on its own history
 * entry instead, for display only; it lasts as long as that entry (the tab's history).
 */
@Injectable({ providedIn: 'root' })
export class SubmittedSignupService {
  private location = inject(Location);
  private addressStore = inject(SignupAddressStore);
  private contactStore = inject(ContactFormStore);
  private enrollment = inject(SignupEnrollmentService);

  /** Keeps the submitted page on the current (/signup) history entry, alongside the router's own state. */
  save(): void {
    const lookup = this.addressStore.lookup();
    const submitted: SubmittedSignup = {
      address: this.addressStore.address(),
      lookup: lookup && withoutToken(lookup),
      ownerRelationship: this.addressStore.ownerRelationship(),
      contact: this.contactStore.contact(),
      signedAt: this.enrollment.signedAt()?.toISOString() ?? null,
    };
    this.location.replaceState(this.location.path(true), '', { ...this.state(), [SUBMITTED_SIGNUP_STATE]: submitted });
  }

  /** Puts a page kept by `save` back in the stores; false when this history entry has none. */
  restore(): boolean {
    const submitted = this.state()?.[SUBMITTED_SIGNUP_STATE] as SubmittedSignup | undefined;
    if (!submitted) return false;
    this.addressStore.address.set(submitted.address);
    // No token: nothing is saved or submitted from the restored page
    this.addressStore.lookup.set(submitted.lookup && { ...submitted.lookup, token: '' });
    this.addressStore.lookupStatus.set('done');
    this.addressStore.ownerRelationship.set(submitted.ownerRelationship);
    this.contactStore.contact.set(submitted.contact);
    if (submitted.signedAt) {
      this.enrollment.state.set('signed');
      this.enrollment.signedAt.set(new Date(submitted.signedAt));
    }
    return true;
  }

  private state(): Record<string, unknown> | null {
    return this.location.getState() as Record<string, unknown> | null;
  }
}
